import React, { forwardRef, useCallback, useEffect } from 'react';
import toArray from '@uppy/utils/lib/toArray';
// @ts-ignore
import match from 'mime-match';

import { Uppy, UppyFileMeta } from '@lyearn/shared/hooks/useUppy';
import { FileInput as HtmlFileInput, useSnackbar } from '@lyearn/ui';
import getFileNameAndExtension from '@/modules/classrooms/modules/files/utils/getFileNameAndExtension';

interface FileInputProps {
  uppy: Uppy | null;
  accept?: string;
  multiple?: boolean | null;
  disabled?: boolean;
  fileMeta?: UppyFileMeta & Object;
  onUploadSuccess?: (file: any) => void;
  addImagePlaceholder?: (url: string) => void;
  onUploadError?: () => void;
  allowDuplicateUploads?: boolean;
}

export const addFiles = (
  files: File[],
  source: string,
  uppy: FileInputProps['uppy'],
  fileMeta: FileInputProps['fileMeta'],
  addImagePlaceholder?: (url: string) => void,
  allowDuplicateUploads?: boolean,
) => {
  const descriptors = files.map((file) => {
    const fileName = file.name.replaceAll(' ', '_');
    const uniqueFileName = allowDuplicateUploads ? `${Date.now()}-${fileName}` : fileName;
    return {
      source,
      name: uniqueFileName,
      type: file.type,
      data: file,
      meta: {
        // also user to upload same file to be in different folder. fileId should be different for different path
        // https://github.com/transloadit/uppy/blob/6f6686177ab716e5b3ba688a9b66809ff08518b5/packages/%40uppy/utils/src/generateFileID.js#L21
        relativePath: (fileMeta as any)?.path ?? (file as any).relativePath,
        ...fileMeta,
      },
      allowDuplicateUploads,
    };
  });

  try {
    // @ts-ignore
    uppy.addFiles(descriptors);
    // if(file.type.includes('image'))

    files.map((file) => {
      if (file.type.includes('image')) {
        addImagePlaceholder?.('');
      }
    });
  } catch (err) {
    uppy?.log(err as string);
  }
};

export const matchFileAccept = (accept: string, file: File) => {
  // ref https://github.com/transloadit/uppy/blob/7599f3abcbd8ff4602be465af5667c23345c9556/packages/%40uppy/core/src/Restricter.js#L44
  const hasMatch = accept.split(',').some((_type) => {
    const type = _type.trim();

    if (type.includes('/')) {
      if (!file.type) return false;
      return match(file.type.replace(/;.*?$/, ''), type);
    }
    const { extension } = getFileNameAndExtension(file.name);
    if (type[0] === '.' && extension) {
      return extension.toLowerCase() === type.slice(1).toLowerCase();
    }
  });
  return !!hasMatch;
};

const FileInput = forwardRef<HTMLInputElement, FileInputProps>((props, ref) => {
  const {
    disabled,
    multiple = false,
    accept,
    uppy,
    fileMeta,
    onUploadSuccess,
    addImagePlaceholder,
    onUploadError,
    allowDuplicateUploads = false,
  } = props;
  const { showErrorSnackbar } = useSnackbar();

  useEffect(() => {
    if (!uppy) return;

    const uploadSuccessHandler = (file: any, response: any) => {
      onUploadSuccess?.({
        ...file,
        url: response.uploadURL,
      });
    };

    const uploadErrorHandler = () => {
      onUploadError?.();
    };

    uppy.on('upload-success', uploadSuccessHandler);
    uppy.on('upload-error', uploadErrorHandler);
    uppy.on('error', uploadErrorHandler);
    uppy.on('restriction-failed', uploadErrorHandler);
    uppy.on('file-added', uploadErrorHandler);
    uppy.on('upload-progress', uploadErrorHandler);

    return () => {
      uppy.off('upload-success', uploadSuccessHandler);
      uppy.off('upload-error', uploadErrorHandler);
      uppy.off('error', uploadErrorHandler);
      uppy.off('restriction-failed', uploadErrorHandler);
      uppy.off('file-added', uploadErrorHandler);
      uppy.off('upload-progress', uploadErrorHandler);
    };
  }, [onUploadError, onUploadSuccess, uppy]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let files = toArray(event.target.files);
      if (accept) {
        // window os allow selecting other files irrespective of accept value
        files = files.filter((file) => matchFileAccept(accept, file));
      }
      addFiles(files, 'FileInput', uppy, fileMeta, addImagePlaceholder, allowDuplicateUploads);
      // We clear the input after a file is selected, because otherwise
      // change event is not fired in Chrome and Safari when a file
      // with the same name is selected.
      // ___Why not use value="" on <input/> instead?
      //    Because if we use that method of clearing the input,
      //    Chrome will not trigger change if we drop the same file twice (Issue #768).
      // @ts-ignore
      event.target.value = null;
    },
    [accept, uppy, fileMeta, addImagePlaceholder, allowDuplicateUploads],
  );

  const onFileSelectErrorHandler = useCallback(() => {
    showErrorSnackbar({
      message: `Selected file type not allowed, allowed file types are ${accept}`,
    });
  }, [showErrorSnackbar, accept]);

  return (
    <HtmlFileInput
      ref={ref}
      hidden
      accept={accept}
      disabled={disabled}
      multiple={multiple!}
      onChange={handleInputChange}
      onFileSelectError={onFileSelectErrorHandler}
    />
  );
});

export default FileInput;
